import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { IconButton, Stack, Typography } from '@mui/material'
import { useContext, useMemo, useState } from 'react'

import { useGetShopDiscounts } from '../../../hooks/useGetShopDiscounts/useGetShopDiscounts'
import { FeatureFlags } from '../../../models/FeatureFlags'
import { AppContext } from '../../../providers/AppProvider/AppProvider'
import { CounterSaleExpanded } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import {
  CreateDiscountDialogProps,
  CreateDiscountDialogUI
} from '../../Estimates/Discount/CreateDiscountDialogUI'
import {
  EditDiscountDialogProps,
  EditDiscountDialogUI
} from '../../Estimates/Discount/EditDiscountDialogUI'
import {
  CustomFeeDialogProps,
  CustomFeeDialogUI
} from '../../Estimates/ServiceCustomFee/CustomFeeDialogUI'

interface Props {
  counterSale: CounterSaleExpanded
  readOnly: boolean
}

const CounterSaleCartDiscount = ({ counterSale, readOnly }: Props) => {
  const { featureFlags } = useContext(AppContext)
  const { discountsMap } = useGetShopDiscounts()
  const customDiscounts = useMemo(
    () =>
      (counterSale?.pricing?.customFeeEntries || []).filter(
        (fee) => (fee.info?.numDollars || 0) < 0
      ),
    [counterSale?.pricing?.customFeeEntries]
  )
  const shopDiscount = useMemo(
    () =>
      counterSale?.entry!.info!.discount &&
      discountsMap.get(counterSale.entry!.info!.discount!),
    [counterSale?.entry, discountsMap]
  )
  const [customFeeDialogProps, setCustomFeeDialogProps] =
    useState<CustomFeeDialogProps>()
  const [editDiscountDialogProps, setEditDiscountDialogProps] =
    useState<EditDiscountDialogProps>()
  const [createDiscountDialogProps, setCreateDiscountDialogProps] =
    useState<CreateDiscountDialogProps>()

  if (!shopDiscount && customDiscounts.length === 0) {
    return (
      <>
        {customFeeDialogProps && (
          <CustomFeeDialogUI
            {...customFeeDialogProps}
            setCustomProps={setCustomFeeDialogProps}
          />
        )}
        {createDiscountDialogProps && (
          <CreateDiscountDialogUI
            {...createDiscountDialogProps}
            setCustomProps={setCreateDiscountDialogProps}
          />
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}>
          <Typography variant="body1">Discount:</Typography>
          {!readOnly && (
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                if (featureFlags && featureFlags[FeatureFlags.ShopDiscount]) {
                  setCreateDiscountDialogProps({
                    counterSaleEntry: counterSale.entry!
                  })
                } else {
                  setCustomFeeDialogProps({
                    type: 'discount',
                    serviceId: undefined,
                    counterSaleId: counterSale.entry!.id!,
                    feeEntry: undefined
                  })
                }
              }}>
              <AddIcon />
            </IconButton>
          )}
        </Stack>
      </>
    )
  }

  return (
    <>
      {customFeeDialogProps && (
        <CustomFeeDialogUI
          {...customFeeDialogProps}
          setCustomProps={setCustomFeeDialogProps}
        />
      )}
      {editDiscountDialogProps && (
        <EditDiscountDialogUI
          {...editDiscountDialogProps}
          setCustomProps={setEditDiscountDialogProps}
        />
      )}
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={2}>
        {customDiscounts.map((fee) => (
          <Stack
            key={fee.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Typography variant="body1">Discount:</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {!readOnly && (
                <IconButton
                  onClick={() => {
                    setCustomFeeDialogProps({
                      type: 'discount',
                      serviceId: undefined,
                      counterSaleId: undefined,
                      feeEntry: fee
                    })
                  }}>
                  <EditIcon />
                </IconButton>
              )}
              <Typography variant="body1">
                {formatCurrency(fee.info!.numDollars! * -1, true)}
              </Typography>
            </Stack>
          </Stack>
        ))}
        {shopDiscount && (
          <Stack
            key={shopDiscount.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Typography variant="body1">Discount:</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {!readOnly && (
                <IconButton
                  onClick={() => {
                    setEditDiscountDialogProps({
                      counterSaleEntry: counterSale.entry!
                    })
                  }}>
                  <EditIcon />
                </IconButton>
              )}
              <Typography variant="body1">
                {formatCurrency(counterSale.pricing!.shopDiscount! * -1, true)}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    </>
  )
}

export default CounterSaleCartDiscount
